import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { isMarriedUser } from "../constants/maritalStatus";
import useUserMaritalStatus from "../hooks/useUserMaritalStatus";
import { assetUrl, apiUrl as buildApiUrl } from "../utils/api";

export default function Header() {
  const navigate = useNavigate();
  const menuRef = useRef(null);

  const userId = localStorage.getItem("user_id");
  const consultantId = localStorage.getItem("consultant_id");
  const currentUserId = userId || consultantId;
  const { maritalStatus, isMarried } = useUserMaritalStatus();

  const [avatar, setAvatar] = useState(() =>
    assetUrl(localStorage.getItem("avatar") || "")
  );
  const [unreadCount, setUnreadCount] = useState(0);
  const [friendRequestCount, setFriendRequestCount] = useState(0);
  const [systemMessageCount, setSystemMessageCount] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  const totalBadgeCount = unreadCount + friendRequestCount + systemMessageCount;

  useEffect(() => {
    const syncAvatar = () => {
      const localAvatar = localStorage.getItem("avatar") || "";
      setAvatar(assetUrl(localAvatar));
    };

    syncAvatar();
    window.addEventListener("avatarUpdated", syncAvatar);
    window.addEventListener("storage", syncAvatar);
    return () => {
      window.removeEventListener("avatarUpdated", syncAvatar);
      window.removeEventListener("storage", syncAvatar);
    };
  }, []);

  useEffect(() => {
    const loadAvatar = async () => {
      let profileUrl = "";
      if (userId) {
        profileUrl = buildApiUrl(`/api/profile/${userId}`);
      } else if (consultantId) {
        profileUrl = buildApiUrl(
          `/api/consultant/profile/${consultantId}?owner_consultant_id=${consultantId}`
        );
      } else {
        setAvatar("");
        return;
      }

      try {
        const res = await fetch(profileUrl);
        const data = await res.json();
        if (res.ok) {
          const avatarPath = data.avatar || "";
          setAvatar(assetUrl(avatarPath));
          localStorage.setItem("avatar", avatarPath);
          if (userId && data.marital_status) {
            localStorage.setItem("marital_status", data.marital_status);
          }
        }
      } catch (err) {
        console.error("加载头像失败:", err);
      }
    };

    loadAvatar();
  }, [currentUserId, userId, consultantId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    if (!userId && !consultantId) {
      setUnreadCount(0);
      setFriendRequestCount(0);
      setSystemMessageCount(0);
      return;
    }

    const loadBadgeCounts = async () => {
      try {
        const requests = [];

        if (userId) {
          requests.push(
            fetch(buildApiUrl(`/api/unread-count?current_user_id=${userId}`)),
            fetch(
              buildApiUrl(
                `/api/friend-request/unread-count?current_user_id=${userId}`
              )
            ),
            fetch(buildApiUrl(`/api/system-messages/unread-count?user_id=${userId}`))
          );
        } else {
          requests.push(
            fetch(
              buildApiUrl(
                `/api/system-messages/unread-count?consultant_id=${consultantId}`
              )
            )
          );
        }

        const responses = await Promise.all(requests);

        if (userId) {
          const [msgRes, requestRes, systemRes] = responses;
          const msgData = await msgRes.json();
          const requestData = await requestRes.json();
          const systemData = await systemRes.json();

          if (msgRes.ok) setUnreadCount(msgData.unread_count || 0);
          if (requestRes.ok) setFriendRequestCount(requestData.count || 0);
          if (systemRes.ok) setSystemMessageCount(systemData.unread_count || 0);
        } else {
          const [systemRes] = responses;
          const systemData = await systemRes.json();
          if (systemRes.ok) setSystemMessageCount(systemData.unread_count || 0);
          setUnreadCount(0);
          setFriendRequestCount(0);
        }
      } catch (err) {
        console.error("获取红点数量失败:", err);
      }
    };

    loadBadgeCounts();

    const timer = setInterval(loadBadgeCounts, 5000);

    window.addEventListener("unreadUpdated", loadBadgeCounts);
    window.addEventListener("friendRequestUpdated", loadBadgeCounts);
    window.addEventListener("systemMessageUpdated", loadBadgeCounts);

    return () => {
      clearInterval(timer);
      window.removeEventListener("unreadUpdated", loadBadgeCounts);
      window.removeEventListener("friendRequestUpdated", loadBadgeCounts);
      window.removeEventListener("systemMessageUpdated", loadBadgeCounts);
    };
  }, [userId, consultantId]);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("user_id");
    localStorage.removeItem("consultant_id");
    localStorage.removeItem("role");
    localStorage.removeItem("name");
    localStorage.removeItem("avatar");
    localStorage.removeItem("marital_status");
    localStorage.removeItem("love_score");
    localStorage.removeItem("current_chat_user");

    setMenuOpen(false);
    setAvatar("");
    navigate("/");
    window.location.reload();
  };

  return (
    <header className="flex justify-between items-center px-10 py-4 bg-white shadow-md">
      <h1
        className="text-2xl font-bold text-pink-600 cursor-pointer"
        onClick={() => navigate("/")}
      >
        💖 婚恋有道
      </h1>

      <nav className="flex gap-3 md:gap-5 text-sm font-medium items-center flex-wrap justify-end">
        <Link to="/" className="hover:text-pink-600">
          首页
        </Link>
        {!(userId && isMarried) && (
          <Link
            to="/discover"
            className="text-rose-600 font-bold hover:text-rose-700"
          >
            寻找对象
          </Link>
        )}
        <Link
          to={
            userId && isMarried ? "/ai/marriage" : "/ai/consultant"
          }
          className="hover:text-pink-600"
        >
          {userId && isMarried ? "婚姻顾问" : "AI咨询师"}
        </Link>
        <Link to="/events" className="hover:text-pink-600">
          交友活动
        </Link>
        <Link to="/consultants" className="hover:text-pink-600">
          真人咨询
        </Link>
        <Link to="/moments" className="hover:text-pink-600">
          会员动态
        </Link>
        <Link to="/book-club" className="hover:text-pink-600">
          读书会
        </Link>
        <Link to="/growth" className="hover:text-pink-600">
          关系成长
        </Link>
        <Link to="/shop" className="hover:text-pink-600">
          生活好物
        </Link>
        <Link to="/courses" className="hover:text-pink-600">
          婚恋学院
        </Link>
        {!consultantId && (
          <Link to="/registerConsultants" className="hover:text-pink-600">
            咨询师入驻
          </Link>
        )}
      </nav>

      {!currentUserId ? (
        <div className="flex gap-3">
          <Link to="/login" className="bg-pink-500 text-white px-4 py-2 rounded-full">登录</Link>
          <Link to="/register" className="border border-pink-500 text-pink-500 px-4 py-2 rounded-full">注册</Link>
        </div>
      ) : (
        <div className="relative" ref={menuRef}>
          <div className="relative cursor-pointer" onClick={() => setMenuOpen((prev) => !prev)}>
            <img
              src={avatar || "https://picsum.photos/40"}
              alt="我的头像"
              className="w-10 h-10 rounded-full object-cover border"
            />
            {totalBadgeCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center border border-white">
                {totalBadgeCount > 99 ? "99+" : totalBadgeCount}
              </span>
            )}
          </div>

          {menuOpen && (
            <div className="absolute right-0 mt-3 w-48 bg-white rounded-xl shadow-lg border py-2 z-50">
              <div className="px-4 py-2 text-xs text-gray-500 border-b bg-pink-50 rounded-t-xl">
                聊天 {unreadCount} 条，好友申请 {friendRequestCount} 条
                {systemMessageCount > 0
                  ? `，系统消息 ${systemMessageCount} 条`
                  : ""}
              </div>

              {userId ? (
                <>
                  <button
                    onClick={() => { setMenuOpen(false); navigate(`/center/`); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-pink-50"
                  >
                    用户中心
                  </button>

                  <button
                    onClick={() => { setMenuOpen(false); navigate(`/center/friends/`); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-pink-50"
                  >
                    我的好友
                  </button>

                  <button
                    onClick={() => { setMenuOpen(false); navigate(`/center/messages/`); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-pink-50 flex justify-between items-center"
                  >
                    <span>系统消息</span>
                    {systemMessageCount > 0 && (
                      <span className="min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                        {systemMessageCount > 99 ? "99+" : systemMessageCount}
                      </span>
                    )}
                  </button>

                  <button
                    onClick={() => { setMenuOpen(false); navigate(`/center/inbox/`); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-pink-50 flex justify-between items-center"
                  >
                    <span>收件箱</span>
                    {friendRequestCount > 0 && (
                      <span className="min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                        {friendRequestCount > 99 ? "99+" : friendRequestCount}
                      </span>
                    )}
                  </button>

                  <button
                    onClick={() => { setMenuOpen(false); navigate(`/center/profile/`); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-pink-50"
                  >
                    资料设置
                  </button>

                  <button
                    onClick={() => { setMenuOpen(false); navigate("/shop/orders"); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-pink-50"
                  >
                    好物订单
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => { setMenuOpen(false); navigate("/consultant"); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-pink-50"
                  >
                    咨询师后台
                  </button>

                  <button
                    onClick={() => { setMenuOpen(false); navigate("/consultant/messages"); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-pink-50 flex justify-between items-center"
                  >
                    <span>系统消息</span>
                    {systemMessageCount > 0 && (
                      <span className="min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                        {systemMessageCount > 99 ? "99+" : systemMessageCount}
                      </span>
                    )}
                  </button>

                  <button
                    onClick={() => { setMenuOpen(false); navigate("/consultant/profile/setup"); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-pink-50"
                  >
                    编辑资料
                  </button>

                  <button
                    onClick={() => { setMenuOpen(false); navigate("/consultant/orders"); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-pink-50"
                  >
                    我的订单
                  </button>
                </>
              )}

              <button
                onClick={() => { setMenuOpen(false); navigate(userId ? "/chat-list" : "/consultant/clients"); }}
                className="w-full text-left px-4 py-2 text-sm hover:bg-pink-50 flex justify-between items-center"
              >
                <span>消息</span>
                {unreadCount > 0 && (
                  <span className="min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>

              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50"
              >
                退出登录
              </button>
            </div>
          )}
        </div>
      )}
    </header>
  );
}